import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './Features.css';

/** Supplied assets in `public/images/features/` (served as-is). */
const FEATURE_ILLUSTRATION_SVGS = [
  '/images/features/icon_App-centric-security.svg',
  '/images/features/icon_Visual-infrastructure.svg',
  '/images/features/icon_Unified-security-risk.svg',
  '/images/features/icon_Intelligent-insights.svg',
  '/images/features/icon_Contextual-prioritization.svg',
  '/images/features/icon_Assisted-remediation.svg'
];

const Features = () => {
  const featuresRef = useRef(null);

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, options);

    const cards = featuresRef.current.querySelectorAll('.feature-card');
    cards.forEach(card => {
      card.classList.add('feature-card-hidden');
      observer.observe(card);
    });

    return () => observer.disconnect();
  }, []);

  const features = [
    {
      title: 'Application-Centric Security',
      description: 'Automatically discover business applications and engineering services across environments. Capture contextual attributes to drive prioritization.'
    },
    {
      title: 'Architecture and owners',
      description: 'Visualize your application\'s cloud infrastructure and code resources with a relationship graph view of your architecture.'
    },
    {
      title: 'Unified Security Risk',
      description: 'Triaging findings from multiple tools is chaotic when each speaks a different format. Normalize to one schema and one view.'
    },
    {
      title: 'Intelligent Insights',
      description: 'AI correlates security findings by remediation path, agents enrich with exposure and attack path analysis to reduce false positives.'
    },
    {
      title: 'Contextual Prioritization',
      description: 'Personalize prioritization by risk policies, crown jewel status, by-design architecture decisions, data and compliance requirements.'
    },
    {
      title: 'Assisted Remediation',
      description: 'Reduce escalation to software engineers by focusing on verified Insights, supported by automated SDLC tools and workflows.'
    }
  ];

  return (
    <section id="features" className="features">
      <div className="page-container">
        <div className="page-section__header">
          <h2>Findings prioritized against business context</h2>
          <p>
            Circumvent discovers Applications and Services from your cloud and code, then maps every finding onto
            that architecture. Ownership, exposure, and blast radius are investigated and validated in context.
          </p>
        </div>
        
        <div className="features-grid" ref={featuresRef}>
          {features.map((feature, index) => (
            <article 
              className="feature-card"
              key={feature.title}
              style={{ animationDelay: `${index * 0.2}s` }}
            >
              <div
                className={
                  FEATURE_ILLUSTRATION_SVGS[index].includes('Assisted-remediation')
                    ? 'feature-illustration feature-illustration--wide'
                    : 'feature-illustration'
                }
              >
                <img
                  src={FEATURE_ILLUSTRATION_SVGS[index]}
                  alt=""
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </article>
          ))}
        </div>

        <div className="features-cta-wrap">
          <Link to="/solutions" className="btn btn-solid features-cta">
            Learn More About Our Solutions
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Features; 